import React from 'react';

interface Prediction {
  average: string;
  min: string;
  max: string;
  average_seconds: number;
  min_seconds: number;
  max_seconds: number;
}

interface PredictionData {
  ticket_key: string;
  prediction: Prediction;
  similar_count: number;
  criteria?: {
    priority?: string;
    issue_type?: string;
    labels?: string[];
  };
  message?: string;
  error?: string;
}

interface PredictionCardProps {
  data: PredictionData;
}

const PredictionCard: React.FC<PredictionCardProps> = ({ data }) => {
  if (data.error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-700">
        <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
          </svg>
          <span className="font-semibold">{data.error}</span>
        </div>
      </div>
    );
  }

  if (data.message) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-700">
        <div className="flex items-center gap-2 text-yellow-700 dark:text-yellow-400">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span className="font-semibold">{data.message}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-4 border border-purple-200 dark:border-gray-600 shadow-sm">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <h3 className="text-lg font-bold text-gray-800 dark:text-white">
          Odhad času - {data.ticket_key}
        </h3>
      </div>

      {/* Main Prediction */}
      <div className="bg-white dark:bg-gray-600 rounded-lg p-4 mb-3">
        <div className="text-center">
          <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">Průměrný čas</div>
          <div className="text-3xl font-bold text-purple-600 dark:text-purple-400 mb-2">
            {data.prediction.average}
          </div>
          <div className="flex items-center justify-center gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Min:</span>
              <span className="ml-1 font-semibold text-gray-700 dark:text-gray-200">{data.prediction.min}</span>
            </div>
            <div className="w-px h-4 bg-gray-300 dark:bg-gray-500"></div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Max:</span>
              <span className="ml-1 font-semibold text-gray-700 dark:text-gray-200">{data.prediction.max}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Visual Range */}
      <div className="mb-3">
        <div className="relative h-8 bg-gray-200 dark:bg-gray-500 rounded-full overflow-hidden">
          <div
            className="absolute h-full bg-gradient-to-r from-green-400 to-yellow-400 dark:from-green-600 dark:to-yellow-600 opacity-50"
            style={{
              left: `${(data.prediction.min_seconds / data.prediction.max_seconds) * 100}%`,
              right: '0%'
            }}
          />
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-1 h-full bg-purple-600 dark:bg-purple-400"
            style={{
              left: `${(data.prediction.average_seconds / data.prediction.max_seconds) * 100}%`
            }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-1">
          <span>{data.prediction.min}</span>
          <span>{data.prediction.max}</span>
        </div>
      </div>

      {/* Metadata */}
      <div className="text-xs text-gray-600 dark:text-gray-300 space-y-1">
        <div className="flex items-center justify-between">
          <span>Podobné tikety:</span>
          <span className="font-semibold">{data.similar_count}</span>
        </div>
        
        {data.criteria && (
          <>
            {data.criteria.priority && (
              <div className="flex items-center justify-between">
                <span>Priorita:</span>
                <span className="font-semibold">{data.criteria.priority}</span>
              </div>
            )}
            {data.criteria.issue_type && (
              <div className="flex items-center justify-between">
                <span>Typ:</span>
                <span className="font-semibold">{data.criteria.issue_type}</span>
              </div>
            )}
            {data.criteria.labels && data.criteria.labels.length > 0 && (
              <div className="flex items-start justify-between">
                <span>Labels:</span>
                <div className="flex flex-wrap gap-1 justify-end max-w-[60%]">
                  {data.criteria.labels.slice(0, 3).map((label, idx) => (
                    <span key={idx} className="bg-gray-200 dark:bg-gray-600 px-2 py-0.5 rounded text-xs">
                      {label}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PredictionCard;
